import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import formStyles from 'modules/Forms/common-form.module.scss'
import i18n from 'i18next'
import urlValidator from 'utils/validators/urlValidator'
import tinValidator from 'utils/validators/tinValidator'
import { numberWithSpace } from 'utils/numberWithSpace'

const BUDGET_MIN = 5000
const RECOMMENDED_BUDGET = 300000

export const AvitoAdsSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    avito_id: Yup.string().required('common::validation_messages.required'),
    tax_number: Yup.string()
      .test(
        'tinValidator',
        'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
        tinValidator,
      )
      .required('common::validation_messages.required'),
    landing_page: Yup.string()
      .test('urlValidator', '', urlValidator)
      .required('common::validation_messages.required')
      .required('common::validation_messages.required'),
    budget: Yup.string()
      .test({
        name: 'min_max',
        test: function (value, ctx) {
          const { path } = this

          if (+value >= BUDGET_MIN) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t(
                'services::forms.register_account.avito-ads.fields.budget.errors.min',
                {
                  number: numberWithSpace(BUDGET_MIN),
                },
              ),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
  })

let CreateAvitoAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  networkType,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
}) => {
  const { t } = useTranslation()

  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <>
      <Typography variant={'Header-60'} className={'margin-bottom-16'}>
        {t('services::networks.' + networkType)}
      </Typography>
      <Typography variant={'Body-20'} color="text-secondary" className={'margin-bottom-16'}>
        {t('services::forms.register_account.form_subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(submitAccountData)} noValidate>
        <Field disabled name="network" hidden fullWidth component={renderInputField} t={t} />
        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.avito-ads.fields.login.label'}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="avito_id"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.avito-ads.fields.avito_id.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.avito-ads.fields.avito_id.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="tax_number"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.avito-ads.fields.tax_number.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.avito-ads.fields.tax_number.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="landing_page"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.avito-ads.fields.landing_page.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.avito-ads.fields.landing_page.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="budget"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.avito-ads.fields.budget.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.avito-ads.fields.budget.tooltip', {
                number: numberWithSpace(RECOMMENDED_BUDGET),
              })}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <SubmitButtons submitting={submitting} onClose={onClose} />
      </form>
    </>
  )
}

CreateAvitoAdsAccountForm = reduxForm({
  form: 'CreateAvitoAdsAccountForm',
  initialValues: {
    login: '',
    avito_id: '',
    tax_number: '',
    landing_page: '',
    budget: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAvitoAdsAccountForm)

CreateAvitoAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateAvitoAdsAccountForm
